import { projectDns } from '@/environment';
import { createTtsAudio } from '@/services/api';
import { DidUser } from '@/services/auth';
import { logger } from '@/services/logger';
import { formatS3Url } from '@/utils/s3';
import { escapeSsmlText } from '@d-id/ssml';
import { Providers, ScriptType } from '@d-id/types';
import { Page } from '../../AdvancedEditor';
import { isV3Model } from '../../utils/breakConversion';
import { getPageProperty, PolotnoStoreActivePageKeys, setPageProperty } from '../polotno-store';
import { getAudioResultUrl } from '../useUploadAudioFile';
import { getTtsResultUrl } from '../useUploadTtsCache';
import { MAX_CACHE_VERSION } from './useScenesPreview';

const getProviderPayload = (provider: Providers, page: Page) => {
    const voiceConfig = getPageProperty(page, PolotnoStoreActivePageKeys.ttsVoiceConfig);
    const language = getPageProperty(page, PolotnoStoreActivePageKeys.ttsSelectedLanguage);
    const voice = getPageProperty(page, PolotnoStoreActivePageKeys.ttsSelectedVoice);

    switch (provider) {
        case Providers.Elevenlabs:
            const { stability, similarity_boost, rate } = voiceConfig;
            return {
                model_id: voice?.config?.modelId,
                voice_config: {
                    stability,
                    similarity_boost,
                    rate,
                },
            };
        case Providers.Amazon:
            return {
                language,
            };
        case Providers.Microsoft:
            return {
                language,
                voice_config: {
                    style: voiceConfig.style,
                    rate: voiceConfig.rate,
                    pitch: voiceConfig.pitch,
                },
            };

        default:
            return {};
    }
};

const isProviderSupportsSsml = (provider: Providers): boolean => {
    return [Providers.Microsoft, Providers.AzureOpenAi].includes(provider);
};

export const getTtsPayload = ({ page, user, projectId }: { page: Page; user?: DidUser | null; projectId: string }) => {
    const voice = getPageProperty(page, PolotnoStoreActivePageKeys.ttsSelectedVoice);

    if (!voice?.provider) {
        throw new Error('Voice provider is not selected');
    }

    const text = getPageProperty(page, PolotnoStoreActivePageKeys.textInput);

    const modelId = voice?.provider === Providers.Elevenlabs ? voice?.config?.modelId : undefined;
    const isV3 = isV3Model(modelId);

    const ssml = isProviderSupportsSsml(voice.provider);

    const script = {
        type: ScriptType.text,
        input: escapeSsmlText(text, true, isV3),
        ssml,
        provider: {
            type: voice?.provider,
            voice_id: voice?.id,
            ...getProviderPayload(voice?.provider, page),
        },
    };

    const payload = {
        script,
        requestWordLevelTimestamps: true,
        result_url: getTtsResultUrl({
            user,
            projectId,
            pageId: page.id,
        }),
    };

    return payload;
};

export const getAudioListenUrl = ({ projectId, page, user }: { projectId: string; page: Page; user?: DidUser | null }) => {
    const selectedScript = getPageProperty(page, PolotnoStoreActivePageKeys.selectedScript);

    let audioS3Url;
    if (selectedScript === ScriptType.text) {
        audioS3Url = getAudioResultUrl({ user, projectId, pageId: page.id });
    } else {
        audioS3Url = getPageProperty(page, PolotnoStoreActivePageKeys.uploadedAudio)?.listenUrl || '';
    }

    const listenUrl = formatS3Url(projectDns, audioS3Url, true);

    const cacheVersion = getPageProperty(page, PolotnoStoreActivePageKeys.ttsCacheVersion);

    return `${listenUrl}?cache_busting=${cacheVersion}`;
};

export const getNewCacheVersion = (page: Page) => {
    return getPageProperty(page, PolotnoStoreActivePageKeys.ttsCacheVersion) > MAX_CACHE_VERSION
        ? 1
        : getPageProperty(page, PolotnoStoreActivePageKeys.ttsCacheVersion) + 1;
};

export const createTtsAudioWithRetry = async (payload: any) => {
    let attempt = 0;
    while (attempt < 3) {
        try {
            return await createTtsAudio(payload);
        } catch (error: any) {
            const shouldRetry =
                (!!error.response?.status && (error.response.status === 403 || error.response.status >= 500)) ||
                error.message === 'Network Error';

            logger.warn(`Failed to create TTS audio.`, {
                attempt: attempt + 1,
                shouldRetry,
                error,
                payload,
                response: error.response,
                data: error.response?.data,
                stringifiedData: JSON.stringify(error.response?.data),
            });

            if (!shouldRetry) {
                throw error;
            }

            attempt++;
            await new Promise(resolve => setTimeout(resolve, 1500));
        }
    }

    throw new Error('Failed to create TTS audio');
};

export const clearAudioCache = async (page: Page) => {
    const audioUrl = getPageProperty(page, PolotnoStoreActivePageKeys.cachedAudio);
    if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
    }

    setPageProperty(page, PolotnoStoreActivePageKeys.cachedAudio)('');
};
